import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { createAssignment, listLectures } from "@/lib/api";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { DEPTHS } from "./constants";
import { toast } from "sonner";

// Create assignment modal — supports two modes:
//   (a) Shared: pick one of your lectures, every student studies the same slides + same quiz
//   (b) Individual: student generates their own lecture from a book title + chapter
export const CreateAssignmentModal = ({ open, onClose, classroom, onCreated, preselectedLectureId = "", preselectedTitle = "" }) => {
  const [mode, setMode] = useState(preselectedLectureId ? "shared" : "individual");
  const [title, setTitle] = useState(preselectedTitle);
  const [book, setBook] = useState("");
  const [chapters, setChapters] = useState("");
  const [edition, setEdition] = useState("");
  const [depth, setDepth] = useState("standard");
  const [minScore, setMinScore] = useState(70);
  const [minMinutes, setMinMinutes] = useState(0);
  const [dueAt, setDueAt] = useState("");
  const [sharedLectureId, setSharedLectureId] = useState(preselectedLectureId);
  const [myLectures, setMyLectures] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setMode(preselectedLectureId ? "shared" : "individual");
    setSharedLectureId(preselectedLectureId);
    setTitle(preselectedTitle);
    listLectures().then((rows) => setMyLectures(rows || [])).catch(() => setMyLectures([]));
    // setState functions and `listLectures` (module import) are stable references —
    // listing them explicitly to satisfy react-hooks/exhaustive-deps without
    // triggering re-runs.
  }, [open, preselectedLectureId, preselectedTitle]);

  const submit = async (e) => {
    e.preventDefault();
    if (saving) return;
    if (mode === "shared" && !sharedLectureId) { toast.error("Pick a lecture from your library"); return; }
    if (mode === "individual" && !book.trim()) { toast.error("Book title is required"); return; }
    setSaving(true);
    try {
      const payload = {
        title: title.trim() || (mode === "shared" ? "Shared lecture" : `${book} — ${chapters || "whole book"}`),
        mode,
        shared_lecture_id: mode === "shared" ? sharedLectureId : null,
        book_title: mode === "individual" ? book.trim() : "",
        chapters: mode === "individual" ? chapters.trim() : "",
        edition: edition.trim(),
        depth,
        min_score: Number(minScore) || 0,
        min_minutes: Number(minMinutes) || 0,
        require_quiz: true,
        due_at: dueAt || null,
      };
      const a = await createAssignment(classroom.id, payload);
      onCreated(a);
      onClose();
      setTitle(""); setBook(""); setChapters(""); setEdition(""); setSharedLectureId("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't create — try again");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg p-0 overflow-hidden max-h-[92vh] overflow-y-auto">
        <DialogTitle className="sr-only">New assignment</DialogTitle>
        <DialogDescription className="sr-only">Create an assignment for your class — either share a lecture you already made, or have each student generate their own.</DialogDescription>
        <form onSubmit={submit}>
          <div className="p-6 pb-4">
            <h2 className="font-serif text-2xl font-bold">New assignment</h2>
            <p className="mt-1 text-sm text-muted-foreground">for <span className="font-semibold">{classroom.name}</span></p>
          </div>
          <div className="px-6 pb-6 space-y-4">
            <div>
              <label className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Assignment style</label>
              <div className="mt-2 grid grid-cols-2 gap-2">
                <button type="button" data-testid="asg-mode-individual" onClick={() => setMode("individual")}
                        className={`rounded-xl border-2 p-3 text-left text-sm transition ${mode === "individual" ? "border-accent bg-accent/10" : "border-border"}`}>
                  <div className="font-semibold">Each student generates their own</div>
                  <div className="text-xs text-muted-foreground mt-1">You specify the book + chapter. Every student generates fresh content.</div>
                </button>
                <button type="button" data-testid="asg-mode-shared" onClick={() => setMode("shared")}
                        className={`rounded-xl border-2 p-3 text-left text-sm transition ${mode === "shared" ? "border-accent bg-accent/10" : "border-border"}`}>
                  <div className="font-semibold">Share a lecture I already made</div>
                  <div className="text-xs text-muted-foreground mt-1">All students study the same slides + same quiz. Grades are fully comparable.</div>
                </button>
              </div>
            </div>

            <div>
              <label className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Title <span className="normal-case tracking-normal font-normal text-muted-foreground/60">(optional)</span></label>
              <input data-testid="asg-title" value={title} onChange={(e) => setTitle(e.target.value)}
                     placeholder='e.g. "Chapter 6 · due Friday"'
                     className="mt-2 w-full rounded-xl border border-input bg-background px-4 py-3 outline-none focus:ring-2 focus:ring-ring/60" />
            </div>

            {mode === "shared" ? (
              <div>
                <label className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Pick a lecture from your library</label>
                <select data-testid="asg-shared-lecture" value={sharedLectureId} onChange={(e) => setSharedLectureId(e.target.value)}
                        className="mt-2 w-full rounded-xl border border-input bg-background px-4 py-3 outline-none focus:ring-2 focus:ring-ring/60">
                  <option value="">— pick a lecture —</option>
                  {myLectures.map((l) => (
                    <option key={l.id} value={l.id}>{l.title} — {l.book_title}{l.chapters ? ` · ${l.chapters}` : ""}</option>
                  ))}
                </select>
                {myLectures.length === 0 && (
                  <p className="mt-2 text-xs text-muted-foreground">No lectures yet — head to Create first, generate one, then come back.</p>
                )}
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Book</label>
                    <input data-testid="asg-book" value={book} onChange={(e) => setBook(e.target.value)}
                           placeholder="Campbell Biology" required={mode === "individual"}
                           className="mt-2 w-full rounded-xl border border-input bg-background px-4 py-3 outline-none focus:ring-2 focus:ring-ring/60" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Chapters</label>
                    <input data-testid="asg-chapters" value={chapters} onChange={(e) => setChapters(e.target.value)}
                           placeholder="Ch 6 or Chs 6-8"
                           className="mt-2 w-full rounded-xl border border-input bg-background px-4 py-3 outline-none focus:ring-2 focus:ring-ring/60" />
                  </div>
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Edition (optional)</label>
                  <input data-testid="asg-edition" value={edition} onChange={(e) => setEdition(e.target.value)}
                         placeholder="12th Edition"
                         className="mt-2 w-full rounded-xl border border-input bg-background px-4 py-3 outline-none focus:ring-2 focus:ring-ring/60" />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Study depth</label>
                  <select data-testid="asg-depth" value={depth} onChange={(e) => setDepth(e.target.value)}
                          className="mt-2 w-full rounded-xl border border-input bg-background px-4 py-3 outline-none focus:ring-2 focus:ring-ring/60">
                    {DEPTHS.map((d) => <option key={d.id} value={d.id}>{d.label}</option>)}
                  </select>
                </div>
              </>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Pass score %</label>
                <input data-testid="asg-min-score" type="number" min={0} max={100}
                       value={minScore} onChange={(e) => setMinScore(e.target.value)}
                       className="mt-2 w-full rounded-xl border border-input bg-background px-4 py-3 outline-none focus:ring-2 focus:ring-ring/60" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Min minutes on lecture</label>
                <input data-testid="asg-min-minutes" type="number" min={0}
                       value={minMinutes} onChange={(e) => setMinMinutes(e.target.value)}
                       className="mt-2 w-full rounded-xl border border-input bg-background px-4 py-3 outline-none focus:ring-2 focus:ring-ring/60" />
              </div>
            </div>

            <div>
              <label className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Due <span className="normal-case tracking-normal font-normal text-muted-foreground/60">(optional)</span></label>
              <input data-testid="asg-due" type="datetime-local" value={dueAt} onChange={(e) => setDueAt(e.target.value)}
                     className="mt-2 w-full rounded-xl border border-input bg-background px-4 py-3 outline-none focus:ring-2 focus:ring-ring/60" />
            </div>

            <button data-testid="asg-submit" type="submit" disabled={saving}
                    className="w-full rounded-xl bg-accent text-accent-foreground py-3.5 font-semibold disabled:opacity-60 hover:opacity-90 transition">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mx-auto" /> : "Post assignment"}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
